import * as React from "react"
import { Link, router } from "@inertiajs/react"
import {
  Bookmark,
  ChevronRight,
  Folder as FolderIcon,
  FolderOpen,
  FolderPlus,
  FilePlus2,
  Pencil,
  Plus,
  Settings2,
  Trash2,
  Upload,
} from "lucide-react"
import { Button } from "@ui/Button"
import { ContextMenu } from "@ui/ContextMenu"
import { Dialog } from "@ui/Dialog"
import { Field } from "@ui/Field"
import { Input } from "@ui/Input"
import { Menu } from "@ui/Menu"
import { ScrollArea } from "@ui/ScrollArea"
import { cn } from "@lib/cn"
import type { ApiRequest, Folder, Project, RequestExampleSummary } from "@lib/inertia-types"
import { ImportCollectionDialog } from "@shared/ImportCollectionDialog"
import { MethodBadge } from "./MethodBadge"

type RequestSummary = Pick<
  ApiRequest,
  "id" | "folder_id" | "name" | "http_method" | "url" | "position" | "examples"
>

interface TreeFolder extends Folder {
  childFolders: TreeFolder[]
  requests: RequestSummary[]
}

function buildTree(folders: Folder[], requests: RequestSummary[]): { roots: TreeFolder[]; rootRequests: RequestSummary[] } {
  const byId = new Map<number, TreeFolder>()
  folders.forEach((f) => byId.set(f.id, { ...f, childFolders: [], requests: [] }))
  const roots: TreeFolder[] = []
  byId.forEach((f) => {
    if (f.parent_id && byId.has(f.parent_id)) byId.get(f.parent_id)!.childFolders.push(f)
    else roots.push(f)
  })
  const rootRequests: RequestSummary[] = []
  requests.forEach((r) => {
    if (r.folder_id && byId.has(r.folder_id)) byId.get(r.folder_id)!.requests.push(r)
    else rootRequests.push(r)
  })
  return { roots, rootRequests }
}

type DialogState =
  | { kind: "new-folder"; parentId: number | null }
  | { kind: "new-request"; folderId: number | null }
  | { kind: "rename-folder"; folder: Folder }
  | { kind: "rename-request"; request: RequestSummary }
  | { kind: "rename-example"; requestId: number; example: RequestExampleSummary }
  | null

// Folder expand/collapse persists per project in localStorage so it survives
// the page remounts that Inertia does on every visit (navigating to a request,
// creating/deleting a folder, saving settings, …). We store the collapsed
// ids since folders default to expanded.
function collapsedKey(slug: string) {
  return `fetch-collapsed:${slug}`
}

// Requests default *collapsed* (their examples are secondary), so — mirroring
// the folder set but inverted — we persist the expanded request ids.
function expandedRequestsKey(slug: string) {
  return `fetch-req-expanded:${slug}`
}

function readIdSet(key: string): Set<number> {
  try {
    const raw = localStorage.getItem(key)
    return new Set<number>(raw ? JSON.parse(raw) : [])
  } catch {
    return new Set<number>()
  }
}

export function RequestTree({
  project,
  folders,
  requests,
  selectedRequestId,
  selectedExampleId = null,
  settingsFolderId = null,
  onFolderSettings,
  onRequestSelected,
}: {
  project: Project
  folders: Folder[]
  requests: RequestSummary[]
  selectedRequestId: number | null
  selectedExampleId?: number | null
  settingsFolderId?: number | null
  onFolderSettings?: (folder: Folder) => void
  onRequestSelected?: () => void
}) {
  const { roots, rootRequests } = React.useMemo(() => buildTree(folders, requests), [folders, requests])
  const [dialog, setDialog] = React.useState<DialogState>(null)
  const [importOpen, setImportOpen] = React.useState(false)
  const [collapsed, setCollapsed] = React.useState<Set<number>>(() => readIdSet(collapsedKey(project.slug)))
  const [expandedRequests, setExpandedRequests] = React.useState<Set<number>>(() =>
    readIdSet(expandedRequestsKey(project.slug)),
  )

  const persistSet = React.useCallback((key: string, next: Set<number>) => {
    try {
      localStorage.setItem(key, JSON.stringify([...next]))
    } catch {
      /* ignore quota/availability errors */
    }
  }, [])

  const toggleFolder = React.useCallback(
    (id: number) => {
      setCollapsed((prev) => {
        const next = new Set(prev)
        next.has(id) ? next.delete(id) : next.add(id)
        persistSet(collapsedKey(project.slug), next)
        return next
      })
    },
    [project.slug, persistSet],
  )

  const setRequestExpanded = React.useCallback(
    (id: number, expanded: boolean) => {
      setExpandedRequests((prev) => {
        const next = new Set(prev)
        expanded ? next.add(id) : next.delete(id)
        persistSet(expandedRequestsKey(project.slug), next)
        return next
      })
    },
    [project.slug, persistSet],
  )

  // Tree edits only touch the tree props (and possibly the open request/example).
  const treeOnly = { preserveScroll: true, only: ["folders", "requests", "selected_request"] }
  const exampleTreeOnly = { preserveScroll: true, only: ["requests", "selected_example"] }

  function deleteFolder(folder: Folder) {
    if (!confirm(`Delete folder "${folder.name}" and everything in it?`)) return
    router.delete(`/projects/${project.slug}/folders/${folder.id}`, treeOnly)
  }

  function deleteRequest(request: RequestSummary) {
    if (!confirm(`Delete request "${request.name}"?`)) return
    router.delete(`/projects/${project.slug}/requests/${request.id}`, treeOnly)
  }

  // Create a new example seeded from the endpoint's current scratchpad params;
  // the controller redirects to (selects) it, so expand the request first.
  function newExample(requestId: number) {
    setRequestExpanded(requestId, true)
    router.post(
      `/projects/${project.slug}/requests/${requestId}/examples`,
      { seed_from_scratchpad: true },
      { preserveScroll: true },
    )
  }

  function deleteExample(requestId: number, example: RequestExampleSummary) {
    if (!confirm(`Delete example "${example.name}"?`)) return
    router.delete(`/projects/${project.slug}/requests/${requestId}/examples/${example.id}`, exampleTreeOnly)
  }

  const requestNodeProps = {
    project,
    selectedRequestId,
    selectedExampleId,
    expandedRequests,
    onToggleExpand: (id: number, expanded: boolean) => setRequestExpanded(id, expanded),
    onRename: (r: RequestSummary) => setDialog({ kind: "rename-request", request: r }),
    onDelete: deleteRequest,
    onNewExample: newExample,
    onRenameExample: (requestId: number, example: RequestExampleSummary) =>
      setDialog({ kind: "rename-example", requestId, example }),
    onDeleteExample: deleteExample,
    onSelect: onRequestSelected,
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between px-3 pb-1 pt-3">
        <span className="text-mini font-semibold uppercase tracking-wide text-cloverhound-gray-600">
          Requests
        </span>
        <Menu.Root>
          <Menu.Trigger
            render={<Button variant="ghost" size="iconXs" aria-label="Add" />}
          >
            <Plus className="size-3.5" aria-hidden="true" />
          </Menu.Trigger>
          <Menu.Content align="end" sideOffset={4}>
            <Menu.Item onClick={() => setDialog({ kind: "new-folder", parentId: null })}>
              <FolderPlus className="size-3.5" aria-hidden="true" />
              New service folder
            </Menu.Item>
            <Menu.Item onClick={() => setDialog({ kind: "new-request", folderId: null })}>
              <FilePlus2 className="size-3.5" aria-hidden="true" />
              New request
            </Menu.Item>
            <Menu.Separator />
            <Menu.Item onClick={() => setImportOpen(true)}>
              <Upload className="size-3.5" aria-hidden="true" />
              Import collection
            </Menu.Item>
          </Menu.Content>
        </Menu.Root>
      </div>

      <ScrollArea className="min-h-0 flex-1" viewportClassName="px-2 pb-3">
        {roots.length === 0 && rootRequests.length === 0 ? (
          <p className="px-2 py-3 text-mini text-cloverhound-gray-600">
            No requests yet. Create a service folder for each API you're
            working with, then add requests inside it.
          </p>
        ) : (
          <div className="flex flex-col gap-0.5">
            {roots.map((folder) => (
              <FolderNode
                key={folder.id}
                folder={folder}
                project={project}
                depth={0}
                selectedRequestId={selectedRequestId}
                selectedExampleId={selectedExampleId}
                settingsFolderId={settingsFolderId}
                collapsed={collapsed}
                expandedRequests={expandedRequests}
                onToggle={toggleFolder}
                onNewRequest={(folderId) => setDialog({ kind: "new-request", folderId })}
                onNewSubfolder={(parentId) => setDialog({ kind: "new-folder", parentId })}
                onRenameFolder={(f) => setDialog({ kind: "rename-folder", folder: f })}
                onDeleteFolder={deleteFolder}
                onFolderSettings={onFolderSettings}
                requestNodeProps={requestNodeProps}
              />
            ))}
            {rootRequests.map((request) => (
              <RequestNode key={request.id} request={request} depth={0} {...requestNodeProps} />
            ))}
          </div>
        )}
      </ScrollArea>

      <TreeDialogs project={project} dialog={dialog} onClose={() => setDialog(null)} />
      <ImportCollectionDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        project={project}
        folders={folders}
        reloadOnly={["folders", "requests", "credentials", "folder_settings", "flash"]}
      />
    </div>
  )
}

// Shared props threaded from RequestTree down to each RequestNode.
type RequestNodeProps = {
  project: Project
  selectedRequestId: number | null
  selectedExampleId: number | null
  expandedRequests: Set<number>
  onToggleExpand: (id: number, expanded: boolean) => void
  onRename: (request: RequestSummary) => void
  onDelete: (request: RequestSummary) => void
  onNewExample: (requestId: number) => void
  onRenameExample: (requestId: number, example: RequestExampleSummary) => void
  onDeleteExample: (requestId: number, example: RequestExampleSummary) => void
  onSelect?: () => void
}

function FolderNode({
  folder,
  project,
  depth,
  selectedRequestId,
  selectedExampleId,
  settingsFolderId,
  collapsed,
  expandedRequests,
  onToggle,
  onNewRequest,
  onNewSubfolder,
  onRenameFolder,
  onDeleteFolder,
  onFolderSettings,
  requestNodeProps,
}: {
  folder: TreeFolder
  project: Project
  depth: number
  selectedRequestId: number | null
  selectedExampleId: number | null
  settingsFolderId: number | null
  collapsed: Set<number>
  expandedRequests: Set<number>
  onToggle: (id: number) => void
  onNewRequest: (folderId: number) => void
  onNewSubfolder: (parentId: number) => void
  onRenameFolder: (folder: Folder) => void
  onDeleteFolder: (folder: Folder) => void
  onFolderSettings?: (folder: Folder) => void
  requestNodeProps: RequestNodeProps
}) {
  const expanded = !collapsed.has(folder.id)
  const indent = { paddingLeft: `${8 + depth * 12}px` }
  const settingsActive = settingsFolderId === folder.id

  return (
    <div>
      <ContextMenu.Root>
        <ContextMenu.Trigger
          render={
            <div
              style={indent}
              className={cn(
                "group flex h-7 w-full items-center gap-1.5 rounded-md pr-1 text-mini font-medium transition-colors duration-quick ease-out-quad",
                settingsActive
                  ? "bg-cloverhound-gray-100 text-cloverhound-gray-900 dark:bg-cloverhound-gray-850 dark:text-cloverhound-gray-50"
                  : "text-cloverhound-gray-700 hover:bg-cloverhound-gray-100 hover:text-cloverhound-gray-900 dark:text-cloverhound-gray-400 dark:hover:bg-cloverhound-gray-850 dark:hover:text-cloverhound-gray-50",
              )}
            />
          }
        >
          <button
            type="button"
            onClick={() => onToggle(folder.id)}
            className="flex min-w-0 flex-1 items-center gap-1.5 text-left focus-visible:outline-none"
            aria-expanded={expanded}
          >
            <ChevronRight
              className={cn(
                "size-3 shrink-0 transition-transform duration-quick ease-out-quad",
                expanded && "rotate-90",
              )}
              aria-hidden="true"
            />
            {expanded ? (
              <FolderOpen className="size-3.5 shrink-0 text-cloverhound-gray-500" aria-hidden="true" />
            ) : (
              <FolderIcon className="size-3.5 shrink-0 text-cloverhound-gray-500" aria-hidden="true" />
            )}
            <span className="truncate">{folder.name}</span>
          </button>
          {onFolderSettings && (
            <button
              type="button"
              onClick={() => onFolderSettings(folder)}
              aria-label={`${folder.name} settings`}
              title="Folder settings"
              className={cn(
                "flex size-5 shrink-0 items-center justify-center rounded-sm transition-colors",
                "hover:bg-cloverhound-gray-200 hover:text-cloverhound-gray-800 dark:hover:bg-cloverhound-gray-800 dark:hover:text-cloverhound-gray-100",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cloverhound-indigo-500",
                settingsActive
                  ? "text-cloverhound-indigo-600 dark:text-cloverhound-indigo-300"
                  : "text-cloverhound-gray-400 group-hover:text-cloverhound-gray-600 dark:text-cloverhound-gray-600 dark:group-hover:text-cloverhound-gray-400",
              )}
            >
              <Settings2 className="size-3.5" aria-hidden="true" />
            </button>
          )}
        </ContextMenu.Trigger>
        <ContextMenu.Content>
          <Menu.Item onClick={() => onNewRequest(folder.id)}>
            <FilePlus2 className="size-3.5" aria-hidden="true" />
            New request
          </Menu.Item>
          <Menu.Item onClick={() => onNewSubfolder(folder.id)}>
            <FolderPlus className="size-3.5" aria-hidden="true" />
            New subfolder
          </Menu.Item>
          <Menu.Separator />
          {onFolderSettings && (
            <Menu.Item onClick={() => onFolderSettings(folder)}>
              <Settings2 className="size-3.5" aria-hidden="true" />
              Settings &amp; environments
            </Menu.Item>
          )}
          <Menu.Item onClick={() => onRenameFolder(folder)}>
            <Pencil className="size-3.5" aria-hidden="true" />
            Rename
          </Menu.Item>
          <Menu.Item
            onClick={() => onDeleteFolder(folder)}
            className="text-cloverhound-red-500 data-highlighted:bg-cloverhound-red-50 dark:data-highlighted:bg-cloverhound-red-500/10"
          >
            <Trash2 className="size-3.5" aria-hidden="true" />
            Delete
          </Menu.Item>
        </ContextMenu.Content>
      </ContextMenu.Root>

      {expanded && (
        <div className="flex flex-col gap-0.5">
          {folder.childFolders.map((child) => (
            <FolderNode
              key={child.id}
              folder={child}
              project={project}
              depth={depth + 1}
              selectedRequestId={selectedRequestId}
              selectedExampleId={selectedExampleId}
              settingsFolderId={settingsFolderId}
              collapsed={collapsed}
              expandedRequests={expandedRequests}
              onToggle={onToggle}
              onNewRequest={onNewRequest}
              onNewSubfolder={onNewSubfolder}
              onRenameFolder={onRenameFolder}
              onDeleteFolder={onDeleteFolder}
              onFolderSettings={onFolderSettings}
              requestNodeProps={requestNodeProps}
            />
          ))}
          {folder.requests.map((request) => (
            <RequestNode key={request.id} request={request} depth={depth + 1} {...requestNodeProps} />
          ))}
        </div>
      )}
    </div>
  )
}

function RequestNode({
  request,
  depth,
  project,
  selectedRequestId,
  selectedExampleId,
  expandedRequests,
  onToggleExpand,
  onRename,
  onDelete,
  onNewExample,
  onRenameExample,
  onDeleteExample,
  onSelect,
}: RequestNodeProps & { request: RequestSummary; depth: number }) {
  const examples = request.examples ?? []
  const hasExamples = examples.length > 0
  const expanded = expandedRequests.has(request.id)
  const isSelectedRequest = request.id === selectedRequestId
  // The request row (scratchpad) is "active" only when the scratchpad — not one
  // of its examples — is the current selection.
  const scratchpadActive = isSelectedRequest && selectedExampleId == null
  const indent = { paddingLeft: `${8 + depth * 12}px` }
  // Align example rows under the request's label (past the chevron), so they
  // read as clearly nested beneath the request rather than a sibling.
  const childIndent = { paddingLeft: `${8 + depth * 12 + 26}px` }

  return (
    <div>
      <ContextMenu.Root>
        <ContextMenu.Trigger
          render={
            <div
              style={indent}
              className={cn(
                "group flex h-7 w-full items-center gap-1 rounded-md pr-2 text-mini font-medium transition-colors duration-quick ease-out-quad",
                scratchpadActive
                  ? "bg-cloverhound-gray-100 text-cloverhound-gray-900 dark:bg-cloverhound-gray-850 dark:text-cloverhound-gray-50"
                  : "text-cloverhound-gray-700 hover:bg-cloverhound-gray-100 hover:text-cloverhound-gray-900 dark:text-cloverhound-gray-400 dark:hover:bg-cloverhound-gray-850 dark:hover:text-cloverhound-gray-50",
              )}
            />
          }
        >
          {hasExamples ? (
            <button
              type="button"
              onClick={() => onToggleExpand(request.id, !expanded)}
              className="flex size-4 shrink-0 items-center justify-center focus-visible:outline-none"
              aria-expanded={expanded}
              aria-label={expanded ? "Collapse examples" : "Expand examples"}
            >
              <ChevronRight
                className={cn(
                  "size-3 shrink-0 text-cloverhound-gray-500 transition-transform duration-quick ease-out-quad",
                  expanded && "rotate-90",
                )}
                aria-hidden="true"
              />
            </button>
          ) : (
            <span className="size-4 shrink-0" aria-hidden="true" />
          )}
          <Link
            href={`/projects/${project.slug}/requests/${request.id}`}
            onClick={() => onSelect?.()}
            preserveState
            preserveScroll
            only={["selected_request", "selected_example"]}
            aria-current={scratchpadActive ? "page" : undefined}
            className="flex min-w-0 flex-1 items-center gap-1.5 self-stretch focus-visible:outline-none"
          >
            <MethodBadge method={request.http_method} />
            <span className="truncate">{request.name}</span>
          </Link>
          <button
            type="button"
            onClick={() => onNewExample(request.id)}
            aria-label={`New example for ${request.name}`}
            title="New example"
            className={cn(
              "flex size-5 shrink-0 items-center justify-center rounded-sm opacity-0 transition-opacity group-hover:opacity-100",
              "text-cloverhound-gray-400 hover:bg-cloverhound-gray-200 hover:text-cloverhound-gray-800 dark:hover:bg-cloverhound-gray-800 dark:hover:text-cloverhound-gray-100",
              "focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cloverhound-indigo-500",
            )}
          >
            <Plus className="size-3.5" aria-hidden="true" />
          </button>
        </ContextMenu.Trigger>
        <ContextMenu.Content>
          <Menu.Item onClick={() => onNewExample(request.id)}>
            <Bookmark className="size-3.5" aria-hidden="true" />
            New example
          </Menu.Item>
          <Menu.Separator />
          <Menu.Item onClick={() => onRename(request)}>
            <Pencil className="size-3.5" aria-hidden="true" />
            Rename
          </Menu.Item>
          <Menu.Item
            onClick={() => onDelete(request)}
            className="text-cloverhound-red-500 data-highlighted:bg-cloverhound-red-50 dark:data-highlighted:bg-cloverhound-red-500/10"
          >
            <Trash2 className="size-3.5" aria-hidden="true" />
            Delete
          </Menu.Item>
        </ContextMenu.Content>
      </ContextMenu.Root>

      {hasExamples && expanded && (
        <div className="flex flex-col gap-0.5">
          {examples.map((example) => {
            const active = selectedExampleId === example.id
            return (
              <ContextMenu.Root key={example.id}>
                <ContextMenu.Trigger
                  render={
                    <Link
                      href={`/projects/${project.slug}/requests/${request.id}/examples/${example.id}`}
                      onClick={() => onSelect?.()}
                      preserveState
                      preserveScroll
                      // Reload selected_request too: the example may live under a
                      // different request than the one currently open.
                      only={["selected_request", "selected_example"]}
                      aria-current={active ? "page" : undefined}
                      style={childIndent}
                      className={cn(
                        "flex h-7 items-center gap-1.5 rounded-md pr-2 text-mini transition-colors duration-quick ease-out-quad",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cloverhound-indigo-500",
                        active
                          ? "bg-cloverhound-gray-100 font-medium text-cloverhound-gray-900 dark:bg-cloverhound-gray-850 dark:text-cloverhound-gray-50"
                          : "text-cloverhound-gray-600 hover:bg-cloverhound-gray-100 hover:text-cloverhound-gray-900 dark:text-cloverhound-gray-500 dark:hover:bg-cloverhound-gray-850 dark:hover:text-cloverhound-gray-50",
                      )}
                    />
                  }
                >
                  <Bookmark className="size-3.5 shrink-0 text-cloverhound-gray-400" aria-hidden="true" />
                  <span className="truncate">{example.name}</span>
                </ContextMenu.Trigger>
                <ContextMenu.Content>
                  <Menu.Item onClick={() => onRenameExample(request.id, example)}>
                    <Pencil className="size-3.5" aria-hidden="true" />
                    Rename
                  </Menu.Item>
                  <Menu.Item
                    onClick={() => onDeleteExample(request.id, example)}
                    className="text-cloverhound-red-500 data-highlighted:bg-cloverhound-red-50 dark:data-highlighted:bg-cloverhound-red-500/10"
                  >
                    <Trash2 className="size-3.5" aria-hidden="true" />
                    Delete
                  </Menu.Item>
                </ContextMenu.Content>
              </ContextMenu.Root>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────
// Create / rename dialogs
// ──────────────────────────────────────────────────────────────────────

function TreeDialogs({
  project,
  dialog,
  onClose,
}: {
  project: Project
  dialog: DialogState
  onClose: () => void
}) {
  const [name, setName] = React.useState("")
  const [submitting, setSubmitting] = React.useState(false)

  React.useEffect(() => {
    if (dialog?.kind === "rename-folder") setName(dialog.folder.name)
    else if (dialog?.kind === "rename-request") setName(dialog.request.name)
    else if (dialog?.kind === "rename-example") setName(dialog.example.name)
    else setName("")
  }, [dialog])

  if (!dialog) return null

  const titles: Record<NonNullable<DialogState>["kind"], string> = {
    "new-folder": dialog.kind === "new-folder" && dialog.parentId ? "New subfolder" : "New service folder",
    "new-request": "New request",
    "rename-folder": "Rename folder",
    "rename-request": "Rename request",
    "rename-example": "Rename example",
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim() || !dialog) return
    setSubmitting(true)
    const done = () => {
      setSubmitting(false)
      onClose()
    }
    const base = `/projects/${project.slug}`
    switch (dialog.kind) {
      case "new-folder":
        router.post(`${base}/folders`, { name, parent_id: dialog.parentId }, {
          preserveScroll: true,
          only: ["folders", "requests", "selected_request"],
          onFinish: done,
        })
        break
      case "new-request":
        router.post(`${base}/requests`, { name, folder_id: dialog.folderId }, {
          preserveScroll: true,
          only: ["folders", "requests", "selected_request"],
          onFinish: done,
        })
        break
      case "rename-folder":
        router.patch(`${base}/folders/${dialog.folder.id}`, { name }, {
          preserveScroll: true,
          only: ["folders", "requests", "selected_request"],
          onFinish: done,
        })
        break
      case "rename-request":
        router.patch(`${base}/requests/${dialog.request.id}`, { name }, {
          preserveScroll: true,
          only: ["folders", "requests", "selected_request"],
          onFinish: done,
        })
        break
      case "rename-example":
        router.patch(`${base}/requests/${dialog.requestId}/examples/${dialog.example.id}`, { name }, {
          preserveScroll: true,
          only: ["requests", "selected_example"],
          onFinish: done,
        })
        break
    }
  }

  const placeholder =
    dialog.kind.includes("folder")
      ? "Accounts API"
      : dialog.kind === "rename-example"
        ? "PS – Shipped"
        : "Get customer balance"

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Content size="sm">
        <form onSubmit={submit}>
          <Dialog.Header>
            <Dialog.Title>{titles[dialog.kind]}</Dialog.Title>
          </Dialog.Header>
          <Dialog.Body>
            <Field label="Name" required>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                placeholder={placeholder}
              />
            </Field>
          </Dialog.Body>
          <Dialog.Footer>
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={submitting} disabled={!name.trim()}>
              {dialog.kind.startsWith("new") ? "Create" : "Rename"}
            </Button>
          </Dialog.Footer>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  )
}
